import { useId, useMemo } from 'react';
import type { Permission } from '../../api/types/permissions';
import { useLabelResolver, type LabelResolver } from '../../hooks/useLabelResolver';
import { OTHER_PERMISSIONS_CATEGORY } from '../../lib/labelConstants';

interface Props {
    permissions: Permission[];
    /** Resolvedor a usar (ej. el del tenant visto desde plataforma); sin él, el del actor. */
    labels?: LabelResolver;
}

interface PermissionEntry {
    key: string;
    name: string;
    description?: string;
}

interface PermissionGroup {
    title: string;
    entries: PermissionEntry[];
}

const UNCATEGORIZED_ALONE = 'Permisos';

/** Agrupa los permisos del rol por la categoría del resolvedor; «Otros permisos» al final. */
function groupByCategory(permissions: Permission[], labels: LabelResolver): PermissionGroup[] {
    const buckets = new Map<string, PermissionEntry[]>();
    for (const key of permissions) {
        const category = labels.category(key);
        const entry = { key, ...labels.permission(key) };
        const bucket = buckets.get(category);
        if (bucket) bucket.push(entry);
        else buckets.set(category, [entry]);
    }
    const byName = (a: PermissionEntry, b: PermissionEntry) => a.name.localeCompare(b.name);
    const others = buckets.get(OTHER_PERMISSIONS_CATEGORY);
    buckets.delete(OTHER_PERMISSIONS_CATEGORY);
    const groups = [...buckets.entries()]
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([title, entries]) => ({ title, entries: entries.sort(byName) }));
    if (others) {
        const title = groups.length > 0 ? OTHER_PERMISSIONS_CATEGORY : UNCATEGORIZED_ALONE;
        groups.push({ title, entries: others.sort(byName) });
    }
    return groups;
}

/** Vista de solo lectura de los permisos de un rol (modal «Ver permisos»). */
export function RolePermissionsView({ permissions, labels }: Props) {
    if (labels) return <RolePermissionsList permissions={permissions} labels={labels} />;
    return <ActorRolePermissionsView permissions={permissions} />;
}

function ActorRolePermissionsView({ permissions }: { permissions: Permission[] }) {
    const labels = useLabelResolver();
    return <RolePermissionsList permissions={permissions} labels={labels} />;
}

function RolePermissionsList({
    permissions,
    labels,
}: {
    permissions: Permission[];
    labels: LabelResolver;
}) {
    const idPrefix = useId();

    const groups = useMemo(() => groupByCategory(permissions, labels), [permissions, labels]);

    if (permissions.length === 0) {
        return (
            <p className="text-sm text-muted-foreground">Este rol no tiene permisos asignados.</p>
        );
    }

    return (
        <div className="space-y-4">
            {groups.map(({ title, entries }, index) => {
                const headingId = `${idPrefix}-group-${String(index)}`;
                return (
                    <section key={title} aria-labelledby={headingId} className="space-y-2">
                        <h3 id={headingId} className="text-sm font-semibold text-muted-foreground">
                            {title}
                        </h3>
                        <ul className="space-y-1 pl-3 border-l">
                            {entries.map(({ key, name, description }) => (
                                <li key={key} className="flex flex-col">
                                    <span className="text-sm font-medium leading-tight">
                                        {name}
                                    </span>
                                    {description && (
                                        <span className="text-xs text-muted-foreground leading-tight">
                                            {description}
                                        </span>
                                    )}
                                </li>
                            ))}
                        </ul>
                    </section>
                );
            })}
        </div>
    );
}
